import { Component, Input, OnInit, OnDestroy, Output, EventEmitter, ViewChild, AfterViewInit, inject } from '@angular/core';
import { ModalController, AlertController, PopoverController, LoadingController } from '@ionic/angular';
import { VisitasService, Visita, VisitasQueryParams, EstadoVisita, TipoVisita, VisitasPaginadasResponse, Productor, visitaTieneSolucion, visitaEsRegistroParcial, ETIQUETA_ESTADO_GUARDADO_PARCIAL, SUFIJO_FILTRO_ESTADO_PARCIAL } from '../../services/visitas.service';
import { HeaderService, UnidadOperativaItem } from '../../services/header.service';
import { AuthStore } from '../../../store/auth.store';
import { PermisosVisitas } from '../../../core/services/autenticacion.service';
import { ToastService } from '../../services/toast.service';
import { Subscription, combineLatest } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { AccionVisita } from '../formulario-visita/formulario-visita.component';
import { VisitaPanelService } from '../../services/visita-panel.service';
import { calcularIdJerarquiaParaCargaUO, esPerfilExtensionista } from '../../utils/perfil.util';
import { esEstadoVisitaFinalizada } from '../../utils/visita-resumen-firmado-adjunto.util';
import { RutaDiaSyncService } from '../../services/ruta-dia-sync.service';
import { abrirODescargarBlob } from '../../utils/abrir-adjunto.util';
import { getClaseTipoVisita } from '../../utils/tipo-visita.utils';

/**
 * Tipos de filtro de fecha disponibles
 */
type FiltroFechaTipo = 'proximos_7_dias' | 'proximos_30_dias' | 'hoy' | 'semana_actual' | 'semana_anterior' | 'mes_actual' | 'mes_anterior' | 'rango_personalizado';

/** Opción del selector de filtro por estado (incluye variante sintética Agendada parcial). */
interface OpcionEstadoFiltro {
  /** Token estable para ion-select (evita mezclar number/string y loops de ionChange). */
  valor: string;
  etiqueta: string;
  claseEstado: string;
}

/**
 * Componente de tarjeta de visitas reutilizable.
 * Muestra una tabla con las próximas visitas con funcionalidad de búsqueda,
 * filtros de fecha, paginación y exportación a CSV.
 */
@Component({
  selector: 'app-tarjeta-visitas',
  templateUrl: './tarjeta-visitas.component.html',
  styleUrls: ['./tarjeta-visitas.component.scss'],
  standalone: false
})
export class TarjetaVisitasComponent implements OnInit, OnDestroy, AfterViewInit {
  private static contadorId = 0;

  /** Título de la tarjeta */
  @Input() titulo: string = 'Próximas Visitas';

  /** 
   * Prefijo del subtítulo de la tarjeta.
   * Se concatenará automáticamente con el nombre de la unidad operativa seleccionada.
   * Ejemplo: "Listado de visitas para:" + " Todas las Unidades Operativas"
   */
  @Input() prefijoSubtitulo: string = 'Listado de visitas para:';

  /** Indica si se debe mostrar el botón "Nueva Visita" */
  @Input() mostrarBotonNuevaVisita: boolean = false;

  /** Indica si se debe mostrar la columna de acciones en la tabla */
  @Input() mostrarColumnaAcciones: boolean = true;

  /** Tamaño de página inicial (cantidad de registros por página) */
  @Input() tamanioPaginaInicial: number = 10;

  /** Texto de información que se mostrará en un popover */
  @Input() informacion: string = '';

  /** Muestra el botón de sincronización local para «Ruta del día» (p. ej. solo en dashboard). */
  @Input() mostrarBotonSincronizarRutaDia = false;

  /** Indica si se permite la descarga de PDF en el detalle de la visita */
  @Input() permitirPdf: boolean = false;

  private readonly visitasService = inject(VisitasService);
  private readonly headerService = inject(HeaderService);
  private readonly toastService = inject(ToastService);
  private readonly authStore = inject(AuthStore);
  private readonly modalController = inject(ModalController);
  private readonly alertController = inject(AlertController);
  private readonly loadingController = inject(LoadingController);
  private readonly popoverController = inject(PopoverController);
  private readonly visitaPanelService = inject(VisitaPanelService);
  private readonly rutaDiaSyncService = inject(RutaDiaSyncService);

  /** Identificador único para el popover de información (evita colisiones cuando hay varias tarjetas) */
  public idUnico = `informacion-tarjeta-visitas-${TarjetaVisitasComponent.contadorId++}`;

  /** Evento emitido cuando se hace clic en una visita */
  @Output() visitaSeleccionada = new EventEmitter<Visita>();

  /** Evento emitido cuando se hace clic en el botón "Nueva Visita" */
  @Output() nuevaVisita = new EventEmitter<void>();

  /** Evento emitido cuando cambian las visitas (por filtros, búsqueda, etc.) */
  @Output() visitasCambiaron = new EventEmitter<Visita[]>();

  // Subtítulo dinámico generado con el nombre de la unidad operativa
  subtituloDinamico: string = '';

  // Lista de unidades operativas para obtener el nombre
  private unidadesOperativas: UnidadOperativaItem[] = [];

  // Propiedades para la tabla de visitas
  visitas: Visita[] = [];
  cargandoVisitas = false;
  
  // Paginación
  paginaActual = 1;
  tamanioPagina = 10;
  totalPaginas = 1;
  totalRegistros = 0;
  
  // Opciones de resultados por página
  opcionesTamanioPagina = [5, 10, 25, 50];
  
  // Búsqueda
  terminoBusqueda = '';
  
  // Filtros de fecha
  filtroFechaSeleccionado: FiltroFechaTipo = 'proximos_30_dias';
  fechaDesde = ''; // Formato yyyy-mm-dd para la API
  fechaHasta = ''; // Formato yyyy-mm-dd para la API
  fechaDesdeISO = ''; // Formato ISO para ion-datetime
  fechaHastaISO = ''; // Formato ISO para ion-datetime
  /** Máximo del ion-datetime: fin del año actual + 5 (Ionic limita por defecto al año en curso). */
  fechaMaximaISO = `${new Date().getFullYear() + 5}-12-31`;
  mostrarRangoPersonalizado = false;

  // Popover de fechas
  popoverFechaDesdeAbierto = false;
  popoverFechaHastaAbierto = false;
  popoverFechaEvent: Event | undefined;
  
  // Opciones de formato para ion-datetime (en español)
  // Formato más compacto para que quepa en una línea
  opcionesFormatoFecha = {
    date: {
      weekday: 'short',
      month: 'short',
      day: '2-digit',
      year: 'numeric'
    }
  };
  
  // Opciones de filtro de fecha
  opcionesFiltroFecha = [
    { valor: 'proximos_30_dias', etiqueta: 'Próximos 30 días' },
    { valor: 'proximos_7_dias', etiqueta: 'Próximos 7 días' },
    { valor: 'hoy', etiqueta: 'Hoy' },
    { valor: 'semana_actual', etiqueta: 'Semana actual' },
    { valor: 'semana_anterior', etiqueta: 'Semana anterior' },
    { valor: 'mes_actual', etiqueta: 'Mes actual' },
    { valor: 'mes_anterior', etiqueta: 'Mes anterior' },
    { valor: 'rango_personalizado', etiqueta: 'Rango de fechas' }
  ];

  // Filtro de estado
  estadosVisita: EstadoVisita[] = [];
  /** Opciones cacheadas del selector (se reconstruyen al cargar estadosVisita). */
  opcionesEstadoFiltro: OpcionEstadoFiltro[] = [];
  /** null = todos; id como string; `${id}-parcial` = Agendada guardado parcial */
  estadoFiltroValor: string | null = null;
  cargandoEstados = false;

  // Filtro de tipo
  tiposVisita: TipoVisita[] = [];
  tipoSeleccionado: number | null = null;
  cargandoTipos = false;

  // Ordenación por columnas (server-side)
  sortBy: string = 'fecha';
  sortOrder: 'asc' | 'desc' = 'asc';
  /** Columnas que permiten ordenar; sortKey es el nombre del campo enviado al API en el param `sort`. */
  readonly columnasOrdenables: { id: string; label: string; sortKey: string }[] = [
    { id: 'usuario', label: 'Usuario INDAP', sortKey: 'usuario' },
    { id: 'unidad', label: 'Unidad Operativa', sortKey: 'jerarquia' },
    { id: 'tipo', label: 'Tipo', sortKey: 'tipo' },
    { id: 'descripcion', label: 'Descripción', sortKey: 'descripcion' },
    { id: 'fecha', label: 'Fecha / Hora', sortKey: 'fecha' },
    { id: 'lugar', label: 'Lugar', sortKey: 'lugar' },
    { id: 'estado', label: 'Estado', sortKey: 'estado' }
  ];

  // Unidad Operativa activa (-1 = Todas las Unidades Operativas)
  private unidadOperativaActiva: number = -1;
  private subscriptions: Subscription = new Subscription();
  /** Suscripción a la última petición de visitas; se cancela al iniciar una nueva para evitar que una respuesta obsoleta actualice total/paginación. */
  private cargaVisitasSub: Subscription | null = null;
  /** Identificador de la última petición; las respuestas con id distinto se ignoran para evitar condición de carrera al navegar entre rutas. */
  private cargaVisitasId = 0;

  /** Permisos de visitas del perfil activo; se actualiza al cambiar de perfil. null = compatibilidad (todo permitido). */
  permisosVisitas: PermisosVisitas | null = null;

  /** true cuando el perfil activo es Extensionista (id_perfil === 1). */
  esExtensionista = false;

  // ── Popover único de acciones (evita bug de Ionic con trigger en *ngFor) ──
  visitaMenuActiva: Visita | null = null;
  menuAccionesAbierto = false;
  menuAccionesEvent: Event | undefined;

  // ── Popover único de contingencia ──────────────────────────────────────────
  popoverContingenciaAbierto = false;
  popoverContingenciaEvent: Event | undefined;
  visitaContingenciaActiva: Visita | null = null;

  ngOnInit() {
    this.tamanioPagina = this.tamanioPaginaInicial;

    // Fechas por defecto sin XHR; una sola carga al alinear unidad + área + perfil (evita visitas canceladas en red).
    this.aplicarFiltroFecha(false);

    this.cargarEstadosVisita();
    this.cargarTiposVisita();

    const authCtxSub = combineLatest([
      this.authStore.unidadOperativaActiva$,
      this.authStore.idRegionSeleccionada$,
      this.authStore.idAreaSeleccionada$,
      this.authStore.perfilActivo$
    ])
      .pipe(
        map(([unidad, region, area, perfil]) => ({
          unidad,
          region,
          area,
          perfilId: perfil?.id_perfil ?? null
        })),
        distinctUntilChanged(
          (a, b) =>
            a.unidad === b.unidad &&
            a.region === b.region &&
            a.area === b.area &&
            a.perfilId === b.perfilId
        )
      )
      .subscribe((ctx) => {
        this.unidadOperativaActiva = ctx.unidad;
        this.esExtensionista = esPerfilExtensionista(this.authStore.obtenerPerfilActivo());
        this.actualizarSubtitulo();
        this.cargarUnidadesOperativas();
        this.paginaActual = 1;
        this.cargarVisitas();
      });
    this.subscriptions.add(authCtxSub);

    const guardadoSub = this.visitaPanelService.guardado$.subscribe(() => {
      this.cargarVisitas();
      this.nuevaVisita.emit();
    });
    this.subscriptions.add(guardadoSub);

    const permisosSub = this.authStore.permisosVisitas$.subscribe(p => {
      this.permisosVisitas = p;
    });
    this.subscriptions.add(permisosSub);
    this.permisosVisitas = this.authStore.obtenerPermisosVisitas();
  }

  ngAfterViewInit() {
    // Configurar observadores para agregar atributos a los popovers cuando se abran
    // Solo si ya tenemos los datos cargados
    if (this.estadosVisita.length > 0 || this.tiposVisita.length > 0) {
      setTimeout(() => this.configurarPopoversConColores(), 500);
    }
  }

  // Variable para almacenar el observer y poder desconectarlo
  private popoverObserver: MutationObserver | null = null;
  private popoversProcesados = new Set<Element>();

  /**
   * Configura los popovers para agregar atributos data-* cuando se abran
   */
  private configurarPopoversConColores(): void {
    // Usar MutationObserver para detectar cuando se abren los popovers
    this.popoverObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement) {
              // Buscar popovers de selectores directamente o dentro del nodo
              let popover: Element | null = null;
              
              if (node.tagName === 'ION-POPOVER' || node.classList?.contains('select-popover')) {
                popover = node;
              } else {
                popover = node.querySelector('ion-popover.select-popover') || 
                         node.querySelector('.select-popover') ||
                         node.querySelector('ion-popover');
              }
              
              if (popover && !this.popoversProcesados.has(popover)) {
                this.popoversProcesados.add(popover);
                // Usar un solo timeout para evitar múltiples procesamientos
                setTimeout(() => {
                  this.agregarAtributosColores(popover!);
                }, 200);
              }
            }
          });
        }
      });
    });

    // Observar cambios en el body para detectar popovers
    this.popoverObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Agrega badges de color a los labels del popover (Tipos y Estados)
   */
  private agregarAtributosColores(popover: Element): void {
    // Filtro estado plan en /gestion: mismo cssClass base `select-popover` pero estilos/contenido propios;
    // "Diseño" / "Implementación" coinciden con tipos de visita y no deben recibir tipo-con-circulo.
    if (popover.classList.contains('select-popover-estado-plan-negocio') || popover.classList.contains('select-popover-checkmark')) {
      return;
    }


    // Buscar items dentro del popover de diferentes maneras
    let items = popover.querySelectorAll('ion-item');
    
    // Si no encuentra items directamente, buscar dentro de ion-select-popover
    if (items.length === 0) {
      const selectPopover = popover.querySelector('ion-select-popover');
      if (selectPopover) {
        items = selectPopover.querySelectorAll('ion-item');
      }
    }
    
    // Si aún no encuentra, buscar en cualquier lugar dentro del popover
    if (items.length === 0) {
      items = popover.querySelectorAll('ion-item');
    }
    
    if (items.length === 0) {
      return;
    }
    
    items.forEach((item) => {
      // Si ya tiene el badge o tipo-con-circulo, no hacer nada
      if (item.querySelector('.badge-selector') || item.querySelector('.tipo-con-circulo')) {
        return;
      }
      
      // Buscar el texto en diferentes lugares dentro del item
      let texto = '';
      let elementoTexto: Element | null = null;
      
      // Obtener el texto completo del item primero
      texto = item.textContent?.trim() || '';
      
      // Buscar el elemento que contiene el texto (puede ser el label o el item mismo)
      const label = item.querySelector('ion-label');
      if (label) {
        elementoTexto = label;
        texto = label.textContent?.trim() || texto;
      } else {
        // Buscar en otros lugares comunes de Ionic
        const itemNative = item.querySelector('.item-native');
        const itemInner = item.querySelector('.item-inner');
        const itemLabel = item.querySelector('[class*="label"]');
        
        elementoTexto = itemNative || itemInner || itemLabel || item;
      }
      
      // Limpiar el texto de espacios y caracteres especiales
      texto = texto.replace(/\s+/g, ' ').trim();
      
      // Saltar opciones como "Todos los estados" o "Todos los tipos"
      if (texto.includes('Todos los')) {
        return;
      }
      
      if (!texto || !elementoTexto) {
        return;
      }
      
      let clase = '';
      let badgeBase = '';

      if (texto === ETIQUETA_ESTADO_GUARDADO_PARCIAL) {
        clase = 'estado-agendada';
        badgeBase = 'badge-estado';
      } else {
        // Verificar si es un estado
        for (const estado of this.estadosVisita) {
          if (estado.descripcion === texto || texto.includes(estado.descripcion)) {
            clase = this.getClaseEstado(estado.descripcion);
            badgeBase = 'badge-estado';
            break;
          }
        }
      }
      
      // Si no es estado, verificar si es un tipo
      // IMPORTANTE: Ordenar por longitud descendente para verificar primero los tipos más específicos
      // Ordenar por longitud descendente para emparejar descripciones más específicas primero
      // (ej: "Asesoría de especialistas individual" antes que "Asesoría base")
      if (!badgeBase) {
        const tiposOrdenados = [...this.tiposVisita].sort((a, b) => b.descripcion.length - a.descripcion.length);
        for (const tipo of tiposOrdenados) {
          // Verificar coincidencia exacta primero, luego si el texto incluye la descripción
          if (tipo.descripcion === texto || texto.includes(tipo.descripcion)) {
            clase = getClaseTipoVisita(tipo.descripcion, tipo.id_tipo_visita);
            badgeBase = 'badge-tipo';
            break;
          }
        }
      }
      
      // Si encontramos clase, mostrar según tipo: círculo+texto para tipos, badge para estados
      if (clase && badgeBase && elementoTexto) {
        elementoTexto.textContent = '';
        if (badgeBase === 'badge-tipo') {
          const wrapper = document.createElement('span');
          wrapper.className = `tipo-con-circulo badge-selector ${badgeBase} ${clase}`;
          const circulo = document.createElement('span');
          circulo.className = 'circulo-tipo';
          const textoSpan = document.createElement('span');
          textoSpan.className = 'texto-tipo';
          textoSpan.textContent = texto;
          wrapper.appendChild(circulo);
          wrapper.appendChild(textoSpan);
          elementoTexto.appendChild(wrapper);
        } else {
          const badge = document.createElement('span');
          badge.className = `badge-selector ${badgeBase} ${clase}`;
          badge.textContent = texto;
          elementoTexto.appendChild(badge);
        }
      }
    });
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    if (this.cargaVisitasSub) {
      this.cargaVisitasSub.unsubscribe();
      this.cargaVisitasSub = null;
    }
    // Desconectar el observer para evitar memory leaks
    if (this.popoverObserver) {
      this.popoverObserver.disconnect();
      this.popoverObserver = null;
    }
    this.popoversProcesados.clear();
  }

  // ========== MÉTODOS PARA UNIDADES OPERATIVAS ==========

  /**
   * Carga las unidades operativas desde la API (solo para listado/nombre).
   * El input es el ID del área seleccionada: -1 = todas las áreas; otro = UOs de esa área.
   */
  private cargarUnidadesOperativas(): void {
    const perfil = this.authStore.obtenerPerfilActivo();
    if (!perfil || perfil.id_perfil == null) {
      this.unidadesOperativas = [];
      this.actualizarSubtitulo();
      return;
    }
    const idJerarquia = calcularIdJerarquiaParaCargaUO(
      perfil,
      this.authStore.obtenerIdRegionSeleccionada(),
      this.authStore.obtenerIdAreaSeleccionada()
    );
    this.headerService.obtenerUnidadesOperativas(perfil.id_perfil, idJerarquia).subscribe({
      next: (unidades) => {
        this.unidadesOperativas = unidades;
        this.actualizarSubtitulo();
      },
      error: (error) => {
        console.error('Error al cargar unidades operativas:', error);
        this.unidadesOperativas = [];
        this.actualizarSubtitulo();
      }
    });
  }

  /**
   * Actualiza el subtítulo dinámico basándose en la unidad operativa activa
   */
  private actualizarSubtitulo(): void {
    const nombreUnidad = this.obtenerNombreUnidadOperativa();
    this.subtituloDinamico = `${this.prefijoSubtitulo} ${nombreUnidad}`;
  }

  /**
   * Obtiene el nombre de la unidad operativa activa
   */
  private obtenerNombreUnidadOperativa(): string {
    if (this.unidadOperativaActiva === -1) {
      return 'Todas las Unidades Operativas';
    }

    const unidad = this.unidadesOperativas.find(u => u.id_jerarquia === this.unidadOperativaActiva);
    return unidad ? unidad.descripcion : 'Unidad Operativa';
  }

  // ========== MÉTODOS PARA ESTADOS DE VISITA ==========

  /**
   * Carga los estados de visita disponibles desde la API
   */
  private cargarEstadosVisita(): void {
    this.cargandoEstados = true;
    this.visitasService.obtenerEstadosVisita().subscribe({
      next: (estados) => {
        this.estadosVisita = estados;
        this.actualizarOpcionesEstadoFiltro();
        this.cargandoEstados = false;
        console.log('Estados cargados:', estados);
        // Configurar popovers después de cargar los datos
        setTimeout(() => this.configurarPopoversConColores(), 100);
      },
      error: (error) => {
        console.error('Error al cargar estados de visita:', error);
        this.estadosVisita = [];
        this.actualizarOpcionesEstadoFiltro();
        this.cargandoEstados = false;
      }
    });
  }

  /**
   * Manejador cuando se hace click en un selector
   */
  onSelectorClick(tipo: 'estado' | 'tipo'): void {
    // Limpiar el set de popovers procesados para permitir reprocesar
    this.popoversProcesados.clear();
    
    // Esperar a que el popover se abra y luego agregar los círculos
    // Usar solo unos pocos intentos estratégicos
    const intentos = [200, 400, 600];
    intentos.forEach((delay) => {
      setTimeout(() => {
        // Buscar popovers de diferentes maneras
        const popovers1 = document.querySelectorAll('ion-popover');
        const popovers2 = document.querySelectorAll('.select-popover');
        const popovers3 = document.querySelectorAll('ion-select-popover');
        
        // Convertir todos a Element[] para evitar problemas de tipos
        const todosPopovers: Element[] = [
          ...Array.from(popovers1),
          ...Array.from(popovers2),
          ...Array.from(popovers3)
        ];
        
        // Eliminar duplicados
        const popoversUnicos = Array.from(new Set(todosPopovers));
        
        if (popoversUnicos.length > 0) {
          popoversUnicos.forEach((popover) => {
            if (!this.popoversProcesados.has(popover)) {
              this.popoversProcesados.add(popover);
              this.agregarAtributosColores(popover);
            }
          });
        }
      }, delay);
    });
  }

  /**
   * Reconstruye las opciones del selector de estado (cache estable para ion-select).
   */
  private actualizarOpcionesEstadoFiltro(): void {
    const opciones: OpcionEstadoFiltro[] = [];
    for (const estado of this.estadosVisita) {
      if(this.titulo=='Próximas Visitas'){
        if (estado.id_estado_visita !== 2 && estado.id_estado_visita !== 3) {
          opciones.push({
            valor: String(estado.id_estado_visita),
            etiqueta: estado.descripcion,
            claseEstado: this.getClaseEstado(estado.descripcion),
          });
        }
      }
      else{
        opciones.push({
            valor: String(estado.id_estado_visita),
            etiqueta: estado.descripcion,
            claseEstado: this.getClaseEstado(estado.descripcion),
          });
      }
      if (estado.descripcion.toLowerCase() === 'agendada') {
        opciones.push({
          valor: `${estado.id_estado_visita}${SUFIJO_FILTRO_ESTADO_PARCIAL}`,
          etiqueta: ETIQUETA_ESTADO_GUARDADO_PARCIAL,
          claseEstado: 'estado-agendada',
        });
      }
    }
    this.opcionesEstadoFiltro = opciones;
  }

  /** Evita ionChange duplicados cuando Ionic re-sincroniza el valor del select. */
  compararValorEstadoFiltro = (a: string | null | undefined, b: string | null | undefined): boolean =>
    this.normalizarValorFiltroEstado(a) === this.normalizarValorFiltroEstado(b);

  /** Normaliza el valor del ion-select (Ionic puede devolver number o string). */
  private normalizarValorFiltroEstado(valor: unknown): string | null {
    if (valor === null || valor === undefined || valor === '') {
      return null;
    }
    return String(valor);
  }

  /** true cuando el filtro activo es la variante Agendada (Guardado Parcial). */
  private esFiltroEstadoGuardadoParcial(valor: string | null = this.estadoFiltroValor): boolean {
    const normalizado = this.normalizarValorFiltroEstado(valor);
    return !!normalizado && normalizado.endsWith(SUFIJO_FILTRO_ESTADO_PARCIAL);
  }

  /**
   * Mapea el filtro de estado seleccionado a query params OpenAPI.
   */
  private aplicarFiltroEstadoAParams(params: VisitasQueryParams): void {
    const valor = this.normalizarValorFiltroEstado(this.estadoFiltroValor);
    if (valor === null) {
      return;
    }
    if (this.esFiltroEstadoGuardadoParcial(valor)) {
      const idStr = valor.slice(0, -SUFIJO_FILTRO_ESTADO_PARCIAL.length);
      const id = Number(idStr);
      if (!Number.isNaN(id)) {
        params.id_estado_visita = id;
        params.es_parcial = true;
      }
      return;
    }
    const id = Number(valor);
    if (!Number.isNaN(id)) {
      params.id_estado_visita = id;
      params.es_parcial = false;
    }
  }

  /**
   * Manejador cuando cambia el filtro de estado.
   * No comparar con estadoFiltroValor: ngModel puede actualizarlo antes que ionChange.
   */
  onFiltroEstadoChange(event: CustomEvent): void {
    const nuevo = this.normalizarValorFiltroEstado(event.detail.value);
    this.estadoFiltroValor = nuevo;
    this.paginaActual = 1;
    this.cargarVisitas();
  }

  /**
   * Limpia el filtro de estado
   */
  limpiarFiltroEstado(): void {
    this.estadoFiltroValor = null;
    this.paginaActual = 1;
    this.cargarVisitas();
  }

  // ========== MÉTODOS PARA TIPOS DE VISITA ==========

  /**
   * Carga los tipos de visita disponibles desde la API
   */
  private cargarTiposVisita(): void {
    this.cargandoTipos = true;
    this.visitasService.obtenerTiposVisita().subscribe({
      next: (tipos) => {
        this.tiposVisita = tipos;
        this.cargandoTipos = false;
        console.log('Tipos cargados:', tipos);
        // Configurar popovers después de cargar los datos
        setTimeout(() => this.configurarPopoversConColores(), 100);
      },
      error: (error) => {
        console.error('Error al cargar tipos de visita:', error);
        this.tiposVisita = [];
        this.cargandoTipos = false;
      }
    });
  }

  /**
   * Manejador cuando cambia el filtro de tipo
   */
  onFiltroTipoChange(event: any): void {
    this.tipoSeleccionado = event.detail.value;
    this.paginaActual = 1;
    this.cargarVisitas();
  }

  /**
   * Limpia el filtro de tipo
   */
  limpiarFiltroTipo(): void {
    this.tipoSeleccionado = null;
    this.paginaActual = 1;
    this.cargarVisitas();
  }

  // ========== MÉTODOS PARA VISITAS ==========

  /**
   * Carga las visitas desde la API con los filtros actuales.
   * Siempre usa el endpoint por perfil y jerarquía: /api/v1/visita/perfil/{id_perfil}/jerarquia/{id_jerarquia}.
   * id_jerarquia = -1 corresponde a "Todas las Unidades Operativas".
   */
  cargarVisitas(): void {
    this.cargandoVisitas = true;

    const params: VisitasQueryParams = {
      page: this.paginaActual,
      size: this.tamanioPagina,
      sort: this.sortBy,
      order: this.sortOrder
    };

    if (this.fechaDesde) {
      params.desde = this.fechaDesde;
    }
    if (this.fechaHasta) {
      params.hasta = this.fechaHasta;
    }
    if (this.terminoBusqueda.trim()) {
      params.search = this.terminoBusqueda.trim();
    }
    this.aplicarFiltroEstadoAParams(params);
    if (this.tipoSeleccionado !== null) {
      params.id_tipo_visita = this.tipoSeleccionado;
    }

    const perfil = this.authStore.obtenerPerfilActivo();
    const idJerarquia = this.authStore.obtenerIdJerarquiaParaEndpoint();

    // El listado de visitas según OpenAPI es GET .../visita/perfil/{id_perfil}/jerarquia/{id_jerarquia}.
    // id_perfil viene de /me (perfiles); el usuario autenticado siempre tiene al menos un perfil.
    // Este branch solo por edge cases (store no hidratado aún, etc.).
    if (perfil?.id_perfil == null) {
      this.visitas = [];
      this.totalRegistros = 0;
      this.totalPaginas = 1;
      this.cargandoVisitas = false;
      this.visitasCambiaron.emit(this.visitas);
      return;
    }

    console.log('Cargando visitas:', { perfil: perfil.id_perfil, id_jerarquia: idJerarquia }, params);

    // Cancelar petición anterior para que una respuesta obsoleta no sobrescriba total al volver de otra ruta (ej. dashboard)
    if (this.cargaVisitasSub) {
      this.cargaVisitasSub.unsubscribe();
      this.cargaVisitasSub = null;
    }
    const idPeticion = ++this.cargaVisitasId;
    let es_inicio = false
    if(this.titulo=='Próximas Visitas')
      es_inicio = true
    this.cargaVisitasSub = this.visitasService.obtenerVisitasPorJerarquia(perfil.id_perfil, idJerarquia, params, es_inicio).subscribe({
      next: (respuesta: VisitasPaginadasResponse) => {
        // Ignorar respuesta si ya se lanzó una petición más reciente (evita condición de carrera al navegar)
        if (idPeticion !== this.cargaVisitasId) {
          return;
        }
        this.cargaVisitasSub = null;

        // Actualizar las visitas con los items de la respuesta
        this.visitas = respuesta.items || [];
        
        // Actualizar información de paginación desde el servidor
        if (respuesta.total !== null && respuesta.total !== undefined) {
          this.totalRegistros = respuesta.total;
        } else {
          this.totalRegistros = 0;
        }
        
        if (respuesta.pages !== null && respuesta.pages !== undefined) {
          this.totalPaginas = respuesta.pages;
        } else {
          // Fallback: calcular basado en total y tamaño de página
          this.totalPaginas = this.totalRegistros > 0 
            ? Math.ceil(this.totalRegistros / this.tamanioPagina) 
            : 1;
        }
        
        // Sincronizar página actual con la respuesta del servidor
        if (respuesta.page !== null && respuesta.page !== undefined) {
          this.paginaActual = respuesta.page;
        }
        
        // Sincronizar tamaño de página con la respuesta del servidor
        if (respuesta.size !== null && respuesta.size !== undefined) {
          this.tamanioPagina = respuesta.size;
        }
        
        console.log('Visitas cargadas:', {
          total: this.totalRegistros,
          pagina: this.paginaActual,
          totalPaginas: this.totalPaginas,
          items: this.visitas.length
        });
        
        this.cargandoVisitas = false;
        // Emitir las visitas para que otros componentes puedan sincronizarse
        this.visitasCambiaron.emit(this.visitas);
      },
      error: (error) => {
        if (idPeticion !== this.cargaVisitasId) {
          return;
        }
        this.cargaVisitasSub = null;
        console.error('Error al cargar visitas:', error);
        this.visitas = [];
        this.totalRegistros = 0;
        this.totalPaginas = 1;
        this.cargandoVisitas = false;
      }
    });
  }

  /**
   * Aplica el filtro de fecha seleccionado.
   * @param recargarLista si es false, solo actualiza el rango (p. ej. antes de la primera carga coordinada en ngOnInit).
   */
  aplicarFiltroFecha(recargarLista = true): void {
    const hoy = new Date();
    let desde: Date;
    let hasta: Date;

    switch (this.filtroFechaSeleccionado) {
      case 'proximos_7_dias':
        desde = new Date(hoy);
        hasta = new Date(hoy);
        hasta.setDate(hasta.getDate() + 7);
        this.mostrarRangoPersonalizado = false;
        break;
        
      case 'proximos_30_dias':
        desde = new Date(hoy);
        hasta = new Date(hoy);
        hasta.setDate(hasta.getDate() + 30);
        this.mostrarRangoPersonalizado = false;
        break;
        
      case 'hoy':
        desde = new Date(hoy);
        hasta = new Date(hoy);
        this.mostrarRangoPersonalizado = false;
        break;
        
      case 'semana_actual':
        // Obtener el lunes de la semana actual
        desde = new Date(hoy);
        const diaSemana = desde.getDay();
        const diferencia = diaSemana === 0 ? -6 : 1 - diaSemana;
        desde.setDate(desde.getDate() + diferencia);
        // Domingo de la semana actual
        hasta = new Date(desde);
        hasta.setDate(hasta.getDate() + 6);
        this.mostrarRangoPersonalizado = false;
        break;
        
      case 'semana_anterior':
        // Obtener el lunes de la semana anterior
        desde = new Date(hoy);
        const diaSemanaAnterior = desde.getDay();
        const diferenciaAnterior = diaSemanaAnterior === 0 ? -6 : 1 - diaSemanaAnterior;
        desde.setDate(desde.getDate() + diferenciaAnterior - 7);
        // Domingo de la semana anterior
        hasta = new Date(desde);
        hasta.setDate(hasta.getDate() + 6);
        this.mostrarRangoPersonalizado = false;
        break;
        
      case 'mes_actual':
        desde = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
        hasta = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
        this.mostrarRangoPersonalizado = false;
        break;
        
      case 'mes_anterior':
        desde = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
        hasta = new Date(hoy.getFullYear(), hoy.getMonth(), 0);
        this.mostrarRangoPersonalizado = false;
        break;
        
      case 'rango_personalizado':
        this.mostrarRangoPersonalizado = true;
        // Si no hay fechas seleccionadas, inicializar con el mes actual
        if (!this.fechaDesde || !this.fechaHasta) {
          desde = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
          hasta = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
          this.fechaDesde = this.formatearFecha(desde);
          this.fechaHasta = this.formatearFecha(hasta);
          // Convertir a UTC para evitar problemas de zona horaria
          const desdeUTC = new Date(Date.UTC(desde.getFullYear(), desde.getMonth(), desde.getDate()));
          const hastaUTC = new Date(Date.UTC(hasta.getFullYear(), hasta.getMonth(), hasta.getDate()));
          this.fechaDesdeISO = desdeUTC.toISOString();
          this.fechaHastaISO = hastaUTC.toISOString();
        } else {
          // Asegurar que las fechas ISO estén sincronizadas
          if (!this.fechaDesdeISO && this.fechaDesde) {
            const [year, month, day] = this.fechaDesde.split('-').map(Number);
            const fechaDesdeUTC = new Date(Date.UTC(year, month - 1, day));
            this.fechaDesdeISO = fechaDesdeUTC.toISOString();
          }
          if (!this.fechaHastaISO && this.fechaHasta) {
            const [year, month, day] = this.fechaHasta.split('-').map(Number);
            const fechaHastaUTC = new Date(Date.UTC(year, month - 1, day));
            this.fechaHastaISO = fechaHastaUTC.toISOString();
          }
        }
        if (recargarLista) {
          this.paginaActual = 1;
          this.cargarVisitas();
        }
        return;
        
      default:
        desde = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
        hasta = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
        this.mostrarRangoPersonalizado = false;
    }

    this.fechaDesde = this.formatearFecha(desde);
    this.fechaHasta = this.formatearFecha(hasta);
    
    // Actualizar también los valores ISO para ion-datetime
    // Asegurar que las fechas se conviertan a medianoche en UTC para evitar problemas de zona horaria
    const desdeUTC = new Date(Date.UTC(desde.getFullYear(), desde.getMonth(), desde.getDate()));
    const hastaUTC = new Date(Date.UTC(hasta.getFullYear(), hasta.getMonth(), hasta.getDate()));
    this.fechaDesdeISO = desdeUTC.toISOString();
    this.fechaHastaISO = hastaUTC.toISOString();

    if (recargarLista) {
      this.paginaActual = 1;
      this.cargarVisitas();
    }
  }

  /**
   * Manejador cuando cambia el filtro de fecha
   */
  onFiltroFechaChange(event: any): void {
    this.filtroFechaSeleccionado = event.detail.value;
    this.aplicarFiltroFecha();
  }

  /**
   * Abre el popover de selección de fecha (desde/hasta).
   * El *ngIf en el ion-datetime fuerza la recreación del componente,
   * lo que hace que el calendario se posicione correctamente en el mes de la fecha seleccionada.
   */
  abrirPopoverFecha(tipo: 'desde' | 'hasta', event: Event): void {
    this.popoverFechaEvent = event;
    if (tipo === 'desde') {
      this.popoverFechaDesdeAbierto = true;
    } else {
      this.popoverFechaHastaAbierto = true;
    }
  }

  /**
   * Se cierra el popover de fecha y se resetea el estado.
   */
  onPopoverFechaDismiss(tipo: 'desde' | 'hasta'): void {
    if (tipo === 'desde') {
      this.popoverFechaDesdeAbierto = false;
    } else {
      this.popoverFechaHastaAbierto = false;
    }
  }

  /**
   * Aplica el rango de fechas personalizado
   */
  aplicarRangoPersonalizado(): void {
    if (this.fechaDesde && this.fechaHasta) {
      this.paginaActual = 1;
      this.cargarVisitas();
    }
  }

  /**
   * Maneja el cambio de fecha "Desde"
   */
  onFechaDesdeChange(event: any): void {
    const fechaISO = event.detail.value;
    if (fechaISO) {
      // Actualizar el valor ISO para el datetime
      this.fechaDesdeISO = fechaISO;
      
      // Convertir de ISO string a formato yyyy-mm-dd
      // ion-datetime devuelve un string ISO, extraer solo la parte de fecha
      const fechaStr = fechaISO.split('T')[0];
      this.fechaDesde = fechaStr;
      
      // Si la fecha "hasta" es anterior a "desde", actualizarla
      if (this.fechaHasta && this.fechaHasta < this.fechaDesde) {
        this.fechaHasta = this.fechaDesde;
        this.fechaHastaISO = fechaISO;
      }
    }
  }

  /**
   * Maneja el cambio de fecha "Hasta"
   */
  onFechaHastaChange(event: any): void {
    const fechaISO = event.detail.value;
    if (fechaISO) {
      // Actualizar el valor ISO para el datetime
      this.fechaHastaISO = fechaISO;
      
      // Convertir de ISO string a formato yyyy-mm-dd
      // ion-datetime devuelve un string ISO, extraer solo la parte de fecha
      const fechaStr = fechaISO.split('T')[0];
      this.fechaHasta = fechaStr;
      
      // Si la fecha "desde" es posterior a "hasta", actualizarla
      if (this.fechaDesde && this.fechaDesde > this.fechaHasta) {
        this.fechaDesde = this.fechaHasta;
        this.fechaDesdeISO = fechaISO;
      }
    }
  }


  /**
   * Busca visitas por el término ingresado
   */
  buscarVisitas(): void {
    this.paginaActual = 1;
    this.cargarVisitas();
  }

  /**
   * Cambia la ordenación al hacer clic en una columna.
   * Si ya está ordenada por esa columna, alterna asc/desc; si no, la establece en asc.
   */
  onSortColumn(columnaId: string): void {
    const col = this.columnasOrdenables.find(c => c.id === columnaId);
    if (!col) return;
    if (this.sortBy === col.sortKey) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = col.sortKey;
      this.sortOrder = 'asc';
    }
    this.paginaActual = 1;
    this.cargarVisitas();
  }

  /**
   * Indica si la columna con el sortKey dado es la que está ordenando actualmente
   */
  isColumnaOrdenActiva(sortKey: string): boolean {
    return this.sortBy === sortKey;
  }

  /**
   * Limpia el término de búsqueda
   */
  limpiarBusqueda(): void {
    this.terminoBusqueda = '';
    this.paginaActual = 1;
    this.cargarVisitas();
  }

  /**
   * Navega a la página anterior
   */
  paginaAnterior(): void {
    if (this.paginaActual > 1) {
      this.paginaActual--;
      this.cargarVisitas();
    }
  }

  /**
   * Navega a la página siguiente
   */
  paginaSiguiente(): void {
    if (this.paginaActual < this.totalPaginas) {
      this.paginaActual++;
      this.cargarVisitas();
    }
  }

  /**
   * Navega a una página específica
   */
  irAPagina(pagina: number): void {
    if (pagina >= 1 && pagina <= this.totalPaginas) {
      this.paginaActual = pagina;
      this.cargarVisitas();
    }
  }

  /**
   * Cambia el tamaño de página y recarga los datos
   */
  onTamanioPaginaChange(event: any): void {
    this.tamanioPagina = event.detail.value;
    this.paginaActual = 1; // Volver a la primera página
    this.cargarVisitas();
  }

  /**
   * Maneja el clic en una fila de visita
   */
  onVisitaClick(visita: Visita): void {
    this.visitaSeleccionada.emit(visita);
  }

  /**
   * Maneja el clic en el botón "Nueva Visita"
   */
  onNuevaVisitaClick(): void {
    this.abrirFormulario('nueva');
  }

  // ── Métodos del popover único de acciones ─────────────────────────────────
  abrirMenuAcciones(event: Event, visita: Visita): void {
    event.stopPropagation();
    this.visitaMenuActiva = visita;
    this.menuAccionesEvent = event;
    this.menuAccionesAbierto = true;
  }

  cerrarMenuAcciones(): void {
    this.menuAccionesAbierto = false;
    this.visitaMenuActiva = null;
    this.menuAccionesEvent = undefined;
  }

  abrirPopoverContingencia(event: Event, _visita: Visita): void {
    event.stopPropagation();
    this.visitaContingenciaActiva = _visita;
    this.popoverContingenciaEvent = event;
    this.popoverContingenciaAbierto = true;
  }

  cerrarPopoverContingencia(): void {
    this.popoverContingenciaAbierto = false;
    this.popoverContingenciaEvent = undefined;
    this.visitaContingenciaActiva = null;
  }

  getDescripcionEmergenciaPopover(): string {
    const descripcion = this.visitaContingenciaActiva?.descripcion_emergencia?.trim();
    return descripcion && descripcion.length > 0
      ? descripcion
      : 'No se estipula descripción de la emergencia';
  }

  // ========== MÉTODOS PARA ACCIONES DEL MENÚ ==========

  /**
   * Abre el panel lateral de visitas (a nivel app, sobre header y menú)
   */
  private abrirFormulario(accion: AccionVisita, visita?: Visita): void {
    this.visitaPanelService.abrir(accion, visita, this.permitirPdf);
  }

  /**
   * Maneja el clic en la opción "Reagendar"
   */
  onReagendarClick(visita: Visita): void {
    this.abrirFormulario('reagendar', visita);
  }

  /**
   * Maneja el clic en la opción "Cancelar"
   */
  async onCancelarClick(visita: Visita): Promise<void> {
    if (visitaTieneSolucion(visita)) {
      return;
    }

    const alert = await this.alertController.create({
      header: 'Cancelar Visita',
      message: `¿Está seguro de que desea cancelar la visita programada para ${this.getNombreUsuarioIndap(visita.productor) || 'este usuario'}?`,
      buttons: [
        {
          text: 'No',
          role: 'cancel',
          cssClass: 'secondary'
        },
        {
          text: 'Sí, Cancelar',
          handler: async () => {
            await this.ejecutarCancelarVisita(visita);
          }
        }
      ]
    });

    await alert.present();
  }

  /**
   * Maneja el clic en la opción "Registrar" (abre el formulario de registro de visita)
   */
  onRegistrarClick(visita: Visita): void {
    this.abrirFormulario('registrar', visita);
  }

  /**
   * Ejecuta la acción de cancelar visita
   */
  private async ejecutarCancelarVisita(visita: Visita): Promise<void> {
    if (!visita.id_visita) {
      await this.toastService.mostrar('Error: No se pudo identificar la visita', 'error');
      return;
    }

    try {
      await new Promise<void>((resolve, reject) => {
        this.visitasService.cancelarVisita(visita.id_visita!).subscribe({
          next: () => resolve(),
          error: (error) => reject(error)
        });
      });

      await this.toastService.mostrar('Visita cancelada correctamente', 'success');
      this.cargarVisitas();
    } catch (error: any) {
      console.error('Error al cancelar visita:', error);
      const mensaje = error?.error?.message || 'Error al cancelar la visita';
      await this.toastService.mostrar(mensaje, 'error');
    }
  }

  /**
   * Maneja el clic en la opción "Ver Detalles"
   */
  onVerDetallesClick(visita: Visita): void {
    this.abrirFormulario('ver-detalles', visita);
  }

  esVisitaFinalizada(estado: string | null | undefined): boolean {
    return esEstadoVisitaFinalizada(estado);
  }

  onSubirResumenFirmadoClick(visita: Visita): void {
    if (!esPerfilExtensionista(this.authStore.obtenerPerfilActivo())) return;
    if (!esEstadoVisitaFinalizada(visita.estado)) return;
    this.abrirFormulario('subir-resumen-firmado', visita);
  }

  /**
   * Formatea una fecha a formato yyyy-mm-dd
   */
  private formatearFecha(fecha: Date): string {
    const year = fecha.getFullYear();
    const month = String(fecha.getMonth() + 1).padStart(2, '0');
    const day = String(fecha.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Formatea una fecha en formato yyyy-mm-dd para mostrarla en el botón
   */
  formatearFechaParaMostrar(fechaStr: string): string {
    if (!fechaStr) return 'Seleccionar fecha';
    const [year, month, day] = fechaStr.split('-');
    const fecha = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return fecha.toLocaleDateString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  /**
   * Formatea una fecha ISO a formato legible con fecha (DD/MM/YY) y hora en formato AM/PM
   */
  formatearFechaVisita(fechaISO: string): string {
    if (!fechaISO) return '-';
    const fecha = this.parseFechaListado(fechaISO);
    if (!fecha) return '-';
    
    // Obtener el día de la semana
    const diaSemana = fecha.toLocaleDateString('es-CL', {
      weekday: 'short'
    });
    
    // Fecha en formato DD/MM/YY (año 2 dígitos, separador slash)
    const fechaFormateada = this.formatearFechaCorta(fecha);
    
    // Formatear la hora manualmente para controlar el formato AM/PM
    let horas = fecha.getHours();
    const minutos = fecha.getMinutes();
    const ampm = horas >= 12 ? 'PM' : 'AM';
    horas = horas % 12;
    horas = horas ? horas : 12; // La hora 0 debe ser 12
    
    const horaFormateada = `${String(horas).padStart(2, '0')}:${String(minutos).padStart(2, '0')} ${ampm}`;
    
    return `${diaSemana} ${fechaFormateada} ${horaFormateada}`;
  }

  /**
   * Formatea una Date a DD/MM/YY (año 2 dígitos, separador slash)
   */
  private formatearFechaCorta(fecha: Date): string {
    const day = String(fecha.getDate()).padStart(2, '0');
    const month = String(fecha.getMonth() + 1).padStart(2, '0');
    const year2 = String(fecha.getFullYear() % 100).padStart(2, '0');
    return `${day}/${month}/${year2}`;
  }

  /**
   * Formatea solo la parte de fecha (día de la semana + fecha DD/MM/YY) para mostrar en la tabla
   */
  getDescripcionVisita(visita: Visita): string {
    const d = visita.descripcion?.trim();
    return d || '-';
  }

  /** Fecha/hora a mostrar en listado: con id_solucion usa fecha; planificada sin solución usa fecha_inicio_planificada. */
  getFechaHoraParaListado(visita: Visita): string {
    if (visitaTieneSolucion(visita)) {
      return visita.fecha || '';
    }
    const estadoLower = visita.estado?.toLowerCase() || '';
    if (estadoLower === 'planificada' && visita.fecha_inicio_planificada?.trim()) {
      return visita.fecha_inicio_planificada.trim();
    }
    return visita.fecha || '';
  }

  private parseFechaListado(fechaISO: string): Date | null {
    if (!fechaISO?.trim()) return null;
    const d = new Date(fechaISO.trim().replace(' ', 'T'));
    return isNaN(d.getTime()) ? null : d;
  }

  formatearSoloFechaVisita(fechaISO: string): string {
    if (!fechaISO) return '-';
    const fecha = this.parseFechaListado(fechaISO);
    if (!fecha) return '-';
    const diaSemana = fecha.toLocaleDateString('es-CL', { weekday: 'short' });
    const fechaFormateada = this.formatearFechaCorta(fecha);
    return `${diaSemana} ${fechaFormateada}`;
  }

  formatearSoloFechasVisita(visita: Visita): string {
    let fechas = "-"
    const fecha = this.parseFechaListado(visita.fecha)
    const fecha_termino = this.parseFechaListado(visita.fecha_termino?? "")
    if(fecha != null && fecha_termino != null){
      fechas = this.formatearFechaCorta(fecha) +' a '+ this.formatearFechaCorta(fecha_termino);
    }
    return fechas;
  }

  /**
   * Formatea solo la parte de hora para mostrar en la tabla
   */
  formatearSoloHoraVisita(fechaISO: string): string {
    if (!fechaISO) return '-';
    const fecha = this.parseFechaListado(fechaISO);
    if (!fecha) return '-';
    let horas = fecha.getHours();
    const minutos = fecha.getMinutes();
    const ampm = horas >= 12 ? 'PM' : 'AM';
    horas = horas % 12;
    horas = horas ? horas : 12;
    return `${String(horas).padStart(2, '0')}:${String(minutos).padStart(2, '0')} ${ampm}`;
  }

  /**
   * Obtiene el nombre completo para la columna "Usuario INDAP": nombre y primer apellido.
   * El RUT se muestra por separado en el template para evitar saltos de línea dentro del RUT.
   */
  getNombreUsuarioIndap(productor: Productor | null | undefined): string {
    if (!productor) return '-';
    const partes = [productor.nombre, productor.primer_apellido].filter(Boolean);
    return partes.length ? partes.join(' ') : '-';
  }

  /**
   * Obtiene el RUT del productor para mostrar entre paréntesis (sin partir el RUT en varias líneas).
   */
  getRutUsuarioIndap(productor: Productor | null | undefined): string | null {
    const rut = productor?.rut?.trim();
    return rut || null;
  }

  /**
   * Etiqueta de estado para listado y export (incluye variante guardado parcial).
   */
  getEtiquetaEstadoListado(visita: Visita): string {
    if (visitaEsRegistroParcial(visita)) {
      return ETIQUETA_ESTADO_GUARDADO_PARCIAL;
    }
    return visita.estado || '-';
  }

  /**
   * Obtiene la clase CSS para el badge de estado
   */
  getClaseEstado(estado: string): string {
    const estadoLower = estado?.toLowerCase() || '';
    switch (estadoLower) {
      case 'agendada':
        return 'estado-agendada';
      case 'planificada':
        return 'estado-planificada';
      case 'finalizada':
      case 'finalizada (pendiente de subir)':
      case 'completada':
      case 'completada (pendiente de subir)':
        return 'estado-finalizada';
      case 'cancelada':
        return 'estado-cancelada';
      case 'reagendada':
        return 'estado-reagendada';
      default:
        return 'estado-default';
    }
  }

  /**
   * Determina si una visita permite todas las acciones o solo "Ver Detalles"
   * @param visita La visita a evaluar
   * @returns true si permite todas las acciones (Reagendar, Cancelar, Registrar), false si solo permite Ver Detalles
   */
  permiteTodasLasAcciones(visita: Visita): boolean {
    const estadoLower = visita.estado?.toLowerCase() || '';
    // Solo "Agendada" permite todas las acciones
    return estadoLower === 'agendada';
  }

  /** Permisos efectivos: siempre lee del store para evitar caché; si no hay permisos, nada permitido. */
  private get perm(): PermisosVisitas {
    const p = this.authStore.obtenerPermisosVisitas();
    if (p) return p;
    return {
      agendar: false,
      cancelar: false,
      eliminar: false,
      registrar: false,
      reagendar: false,
      ver_detalles: false
    };
  }

  /** Crear nueva visita se rige por el permiso agendar. */
  puedeCrear(): boolean {
    return this.perm.agendar;
  }

  puedeEliminar(visita: Visita): boolean {
    return this.perm.eliminar && this.permiteTodasLasAcciones(visita);
  }

  puedeVerDetalles(): boolean {
    return this.perm.ver_detalles;
  }

  puedeReagendar(visita: Visita): boolean {
    return this.perm.reagendar && this.permiteTodasLasAcciones(visita);
  }

  /** Muestra la opción "Agendar" cuando el estado es Planificada (misma acción que Reagendar). */
  mostrarOpcionAgendar(visita: Visita): boolean {
    const estadoLower = visita.estado?.toLowerCase() || '';
    return estadoLower === 'planificada' && !!this.perm.reagendar;
  }

  puedeCancelar(visita: Visita): boolean {
    return this.perm.cancelar && this.permiteTodasLasAcciones(visita);
  }

  /** Cancelar visible pero no ejecutable cuando la visita está vinculada a una solución. */
  cancelarEstaDeshabilitado(visita: Visita): boolean {
    return visitaTieneSolucion(visita);
  }

  puedeRegistrar(visita: Visita): boolean {
    return this.perm.registrar && this.permiteTodasLasAcciones(visita);
  }

  /** true si el perfil tiene al menos un permiso de visitas (para mostrar columna/acciones). */
  puedeAlgunaAccionVisitas(): boolean {
    const p = this.perm;
    return p.agendar || p.cancelar || p.eliminar || p.registrar || p.reagendar || p.ver_detalles;
  }

  /**
   * Clase CSS para badge/círculo de tipo (id prioritario; descripción nueva o legacy).
   */
  getClaseTipo(tipo?: string | null, idTipoVisita?: number | null): string {
    return getClaseTipoVisita(tipo, idTipoVisita);
  }

  /**
   * Genera un array de números de página para la paginación
   */
  getPaginasArray(): number[] {
    const paginas: number[] = [];
    const maxPaginasVisibles = 5;
    
    let inicio = Math.max(1, this.paginaActual - Math.floor(maxPaginasVisibles / 2));
    let fin = Math.min(this.totalPaginas, inicio + maxPaginasVisibles - 1);
    
    // Ajustar inicio si fin está en el límite
    if (fin - inicio < maxPaginasVisibles - 1) {
      inicio = Math.max(1, fin - maxPaginasVisibles + 1);
    }
    
    for (let i = inicio; i <= fin; i++) {
      paginas.push(i);
    }
    
    return paginas;
  }

  async confirmarSincronizarRutaDelDia(): Promise<void> {
    const alert = await this.alertController.create({
      header: 'Sincronizar Ruta del Día',
      message:
        'Se sincronizarán al dispositivo móvil las fichas de Usuarios INDAP de mis Unidades Operativas, Planes de Negocio vigentes, las visitas agendadas para el día actual y, por cada productor, la visita vigente en contexto y hasta tres visitas de historial (en conjunto, hasta cuatro visitas por productor desde el día actual hacia atrás), cualquiera sea su estado.',
      buttons: [
        { text: 'Cancelar', role: 'cancel' },
        {
          text: 'Sincronizar',
          handler: () => {
            void this.ejecutarSincronizarRutaDelDia();
          }
        }
      ]
    });
    await alert.present();
  }

  private async ejecutarSincronizarRutaDelDia(): Promise<void> {
    const loading = await this.loadingController.create({
      message: 'Sincronizando ruta del día...'
    });
    await loading.present();

    const actualizarProgreso = (mensaje: string): void => {
      loading.message = mensaje;
    };

    try {
      await this.rutaDiaSyncService.ejecutarSincronizacion(actualizarProgreso);
      const alert = await this.alertController.create({
        header: 'Ruta del día sincronizada',
        message:
          'Los datos quedaron guardados en este dispositivo. ' +
          'Cuando esté sin conexión, abra el menú lateral y use «Ruta del día» para consultar la copia local. ' +
          'Si en campo quedaron registros por subir al servidor, en el dashboard verá la sección «Pendientes de Subir». ' +
          'Pulse Aceptar para continuar.',
        backdropDismiss: false,
        buttons: [{ text: 'Aceptar', role: 'confirm' }]
      });
      await alert.present();
    } catch (e) {
      console.error(e);
      await this.toastService.mostrar('No se pudo completar la sincronización.', 'error');
    } finally {
      try {
        await loading.dismiss();
      } catch {
        // El overlay puede estar ya cerrado
      }
    }
  }

  /**
   * Exporta las visitas filtradas (todas las páginas) a un archivo CSV.
   */
  async exportarCSV(): Promise<void> {
    const perfil = this.authStore.obtenerPerfilActivo();
    const idJerarquia = this.authStore.obtenerIdJerarquiaParaEndpoint();

    if (perfil?.id_perfil == null) {
      await this.toastService.mostrar('No se pudo identificar el perfil para exportar', 'warning');
      return;
    }

    // Configurar los mismos filtros que en la tabla
    const params: VisitasQueryParams = {
      sort: this.sortBy,
      order: this.sortOrder
    };

    if (this.fechaDesde) {
      params.desde = this.fechaDesde;
    }
    if (this.fechaHasta) {
      params.hasta = this.fechaHasta;
    }
    if (this.terminoBusqueda.trim()) {
      params.search = this.terminoBusqueda.trim();
    }
    this.aplicarFiltroEstadoAParams(params);
    if (this.tipoSeleccionado !== null) {
      params.id_tipo_visita = this.tipoSeleccionado;
    }

    try {
      await this.toastService.mostrar('Generando archivo CSV con todos los registros...', 'info');
      
      this.visitasService.obtenerVisitasExport(perfil.id_perfil, idJerarquia, params).subscribe({
        next: async (todasLasVisitas) => {
          if (todasLasVisitas.length === 0) {
            await this.toastService.mostrar('No hay datos para exportar con los filtros actuales', 'warning');
            return;
          }

          // Definir las cabeceras del CSV
          const cabeceras = ['Usuario INDAP', 'Unidad Operativa', 'Tipo', 'Descripción', 'Fecha', 'Lugar', 'Estado'];
          
          // Convertir las visitas a filas CSV
          const filas = todasLasVisitas.map(visita => [
            this.escaparValorCSV(this.getNombreUsuarioIndap(visita.productor) || '-'),
            this.escaparValorCSV(visita.productor.jerarquia.descripcion || '---'),
            this.escaparValorCSV(visita.tipo || '-'),
            this.escaparValorCSV(this.getDescripcionVisita(visita) === '-' ? '' : this.getDescripcionVisita(visita)),
            this.formatearFechaVisita(this.getFechaHoraParaListado(visita)),
            this.escaparValorCSV(visita.lugar || '-'),
            this.escaparValorCSV(this.getEtiquetaEstadoListado(visita))
          ]);

          // Construir el contenido CSV
          const contenidoCSV = [
            cabeceras.join(';'),
            ...filas.map(fila => fila.join(';'))
          ].join('\n');

          // Agregar BOM para que Excel reconozca UTF-8 correctamente
          const BOM = '\uFEFF';
          const csvConBOM = BOM + contenidoCSV;

          // Crear el blob y descargar
          const blob = new Blob([csvConBOM], { type: 'text/csv;charset=utf-8;' });
          const fechaActual = new Date();
          const nombreArchivo = `visitas_${fechaActual.getFullYear()}${String(fechaActual.getMonth() + 1).padStart(2, '0')}${String(fechaActual.getDate()).padStart(2, '0')}.csv`;
          abrirODescargarBlob(blob, nombreArchivo, blob.type, this.toastService);

          await this.toastService.mostrar(`Archivo CSV con ${todasLasVisitas.length} registros descargado correctamente`, 'success');
        },
        error: async (err) => {
          console.error('Error al exportar CSV:', err);
          await this.toastService.mostrar('Error al obtener los datos para la exportación', 'error');
        }
      });
    } catch (error) {
      console.error('Error al exportar CSV:', error);
      await this.toastService.mostrar('Error inesperado al exportar el archivo', 'error');
    }
  }

  /**
   * Escapa valores para CSV (maneja comas, comillas y saltos de línea)
   */
  private escaparValorCSV(valor: string): string {
    if (!valor) return '';
    
    // Si el valor contiene punto y coma, comillas o saltos de línea, envolverlo en comillas
    if (valor.includes(';') || valor.includes('"') || valor.includes('\n') || valor.includes('\r')) {
      // Escapar comillas duplicándolas
      return `"${valor.replace(/"/g, '""')}"`;
    }
    
    return valor;
  }
}
